import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { useTranslation } from '../../../i18n';

// Each occurrence of `{{gap}}` in the text is a distinct blank the student
// must fill. Admins commonly reuse the same marker (e.g. `{{is}}` several
// times) as a shortcut where the marker doubles as the answer — so we key
// state by occurrence, not by raw gap id, or every duplicate input would
// share state and echo each other as the student types.
const gapKey = (gapId, occurrence) => `${gapId}#${occurrence}`;
const baseGapId = (key) => String(key || '').split('#')[0];

const parseTextSegments = (text) => {
  if (!text) return [];
  const segments = [];
  // Matches: {{gap_id}}, ***bold+italic***, **bold**, *italic*
  // gap_id allows any chars except { } and newlines (so apostrophes, spaces,
  // Cyrillic, etc. work — e.g. `{{shouldn't}}` with curly apostrophe).
  const regex = /\{\{([^{}\n\r]+?)\}\}|\*\*\*([^*\n]+?)\*\*\*|\*\*([^*\n]+?)\*\*|\*([^*\n]+?)\*/g;
  const gapCounts = {};
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    }
    if (m[1] !== undefined) {
      const gapId = m[1];
      const occurrence = gapCounts[gapId] || 0;
      gapCounts[gapId] = occurrence + 1;
      segments.push({ type: 'gap', gapId, key: gapKey(gapId, occurrence) });
    } else if (m[2] !== undefined) {
      segments.push({ type: 'bolditalic', value: m[2] });
    } else if (m[3] !== undefined) {
      segments.push({ type: 'bold', value: m[3] });
    } else if (m[4] !== undefined) {
      segments.push({ type: 'italic', value: m[4] });
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return segments;
};

// Smart quotes, modifier letters, backticks, and acute accents all read as
// apostrophes to the eye. iOS/macOS auto-substitute ' → ' as the student
// types, so a stored "shouldn't" (ASCII) would never match a typed
// "shouldn't" (curly) without this fold.
const normalize = (s) => (s || '')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[‘’ʼ`´]/g, "'");

// Return one key per blank occurrence in the text (e.g. `is#0`, `is#1`, ...)
// so answers/results dicts can address each blank independently even when the
// admin reuses the same `{{gap}}` marker.
export const parseFillGapsIds = (text) => {
  if (!text) return [];
  const keys = [];
  const gapCounts = {};
  const regex = /\{\{([^{}\n\r]+?)\}\}/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    const gapId = m[1];
    const occurrence = gapCounts[gapId] || 0;
    gapCounts[gapId] = occurrence + 1;
    keys.push(gapKey(gapId, occurrence));
  }
  return keys;
};

// Resolve accepted answers for a gap. Prefer explicitly configured
// `correctAnswers`; if none are set, fall back to the gap ID with
// `_`/`-` turned into spaces. This lets admins write `{{was_played}}`
// straight in the text as a shortcut without also filling the config
// panel — the marker doubles as the answer.
export const gapCorrectAnswers = (gap, gapId) => {
  const configured = Array.isArray(gap?.correctAnswers)
    ? gap.correctAnswers.filter((a) => (a || '').toString().trim().length > 0)
    : [];
  if (configured.length > 0) return configured;
  return [String(gapId || '').replace(/[_-]/g, ' ')];
};

// Grade fill-gaps answers against the element's content. Iterates over
// gap IDs found in the text (not just `content.gaps`) so exercises whose
// authors typed markers directly still grade correctly.
export const computeFillGapsResults = (content, answers) => {
  const text = content?.text || '';
  const gaps = Array.isArray(content?.gaps) ? content.gaps : [];
  const keys = parseFillGapsIds(text);
  const perGapResults = {};
  keys.forEach((key) => {
    const gapId = baseGapId(key);
    const gap = gaps.find((g) => g.id === gapId);
    const correct = gapCorrectAnswers(gap, gapId);
    const userVal = normalize(answers?.[key]);
    perGapResults[key] = userVal.length > 0 && correct.some((a) => normalize(a) === userVal);
  });
  const isCorrect = keys.length > 0 && Object.values(perGapResults).every((v) => v === true);
  return { perGapResults, isCorrect };
};

const FillGapsElement = ({
  element,
  isEditor = false,
  studentView = false,
  answers: externalAnswers = null,
  showResult = false,
  perGapResults = null,
  onAnswersChange,
  onCheck,
  onRetry,
  disabled = false,
  hideCheckButton = false,
  onFitHeight,
}) => {
  const { t } = useTranslation();
  const { content, style } = element;
  const {
    text = '',
    gaps = [],
    allowRetry = false,
    fontSize = 18,
    fontFamily = 'Inter',
    textColor = '#1f2937',
    gapColor = '#2A4D25',
    gapTextColor,
    inputBgColor = '#ffffff',
    buttonColor = '#2A4D25',
  } = content || {};
  const gapInputTextColor = gapTextColor || textColor;
  const backgroundColor = style?.backgroundColor || '#ffffff';

  const segments = useMemo(() => parseTextSegments(text), [text]);

  const gapMinWidth = useMemo(() => {
    if (!gaps || gaps.length === 0) return 100;
    if (typeof document === 'undefined') {
      const longest = gaps.reduce((mx, g) => {
        const first = Array.isArray(g.correctAnswers) && g.correctAnswers[0] ? g.correctAnswers[0] : '';
        return Math.max(mx, first.length);
      }, 0);
      return Math.max(80, Math.round(longest * fontSize * 0.55) + 28);
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = `500 ${fontSize}px "${fontFamily}", Inter, system-ui, sans-serif`;
    let maxW = 0;
    gaps.forEach((g) => {
      const first = Array.isArray(g.correctAnswers) && g.correctAnswers[0] ? g.correctAnswers[0] : '';
      const w = ctx.measureText(first).width;
      if (w > maxW) maxW = w;
    });
    return Math.max(80, Math.ceil(maxW) + 32);
  }, [gaps, fontSize, fontFamily]);

  const [answers, setAnswers] = useState(externalAnswers || {});
  useEffect(() => {
    const external = externalAnswers || {};
    // Read-only viewers (teacher during a live lesson, editor preview, disabled
    // state, or after check) have no local edits to protect — always mirror
    // the latest external value so polled updates stay in sync.
    if (disabled || isEditor || showResult) {
      setAnswers(external);
      return;
    }
    setAnswers(prev => {
      // Student is actively typing: adopt external only on initial async load
      // (local still empty) or an external reset (external cleared). Keeping
      // local authoritative otherwise prevents stale prop echoes from wiping
      // recently typed characters when the student types faster than the
      // parent's state round-trip.
      const prevEmpty = !prev || Object.keys(prev).length === 0;
      const externalEmpty = Object.keys(external).length === 0;
      if (prevEmpty && !externalEmpty) return external;
      if (externalEmpty) return {};
      return prev;
    });
  }, [externalAnswers, disabled, isEditor, showResult]);

  const scrollRef = useRef(null);
  const contentRef = useRef(null);

  // In the editor, keep the element's height glued to the intrinsic text
  // height so the frame never has trailing whitespace and never clips. The
  // user still controls WIDTH (via corner + left/right handles); height is
  // derived. Fires on content change and on width change (text re-wraps).
  const currentHeightPct = element.size?.height || 0;
  const currentHeightPctRef = useRef(currentHeightPct);
  useEffect(() => { currentHeightPctRef.current = currentHeightPct; }, [currentHeightPct]);
  const onFitHeightRef = useRef(onFitHeight);
  useEffect(() => { onFitHeightRef.current = onFitHeight; }, [onFitHeight]);

  useLayoutEffect(() => {
    if (!isEditor) return undefined;
    const container = scrollRef.current;
    const content = contentRef.current;
    if (!container || !content) return undefined;

    const measure = () => {
      const fn = onFitHeightRef.current;
      const pct = currentHeightPctRef.current;
      if (typeof fn !== 'function' || !pct || pct <= 0) return;
      const containerH = container.clientHeight;
      if (containerH <= 0) return;
      // Padding inside scrollRef (p-5 = 20px each side).
      const paddingH = 40;
      const needed = content.offsetHeight + paddingH;
      const target = pct * (needed / containerH);
      if (Math.abs(target - pct) < 0.5) return;
      fn(target);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(content);
    ro.observe(container);
    return () => ro.disconnect();
  }, [isEditor, segments]);

  const updateAnswers = (next) => {
    setAnswers(next);
    onAnswersChange?.(next);
  };

  const handleInputChange = (gapId, value) => {
    if (disabled || showResult || isEditor) return;
    const next = { ...answers, [gapId]: value };
    updateAnswers(next);
  };

  const handleCheck = () => onCheck?.(answers);
  const handleRetry = () => {
    setAnswers({});
    onRetry?.();
  };

  const textGapKeys = useMemo(() => parseFillGapsIds(text), [text]);
  const allFilled = textGapKeys.length > 0
    && textGapKeys.every((key) => (answers[key] || '').trim().length > 0);
  const allCorrect = perGapResults && Object.keys(perGapResults).length > 0
    && Object.values(perGapResults).every((v) => v === true);

  const renderGap = (key) => {
    const gapId = baseGapId(key);
    const gap = gaps.find((g) => g.id === gapId);
    const value = answers[key] || '';
    const gapResult = showResult && perGapResults ? perGapResults[key] : null;
    const firstCorrect = gap && Array.isArray(gap.correctAnswers) && gap.correctAnswers[0]
      ? gap.correctAnswers[0]
      : '';

    let borderColor = gapColor;
    let bg = inputBgColor;
    let color = gapInputTextColor;
    if (gapResult === true) {
      borderColor = '#22c55e';
      bg = '#dcfce7';
      color = '#166534';
    } else if (gapResult === false) {
      borderColor = '#ef4444';
      bg = '#fee2e2';
      color = '#991b1b';
    }

    return (
      <input
        key={key}
        type="text"
        value={isEditor ? '' : value}
        onChange={(e) => handleInputChange(key, e.target.value)}
        placeholder={isEditor ? (firstCorrect || '...') : ''}
        disabled={isEditor || disabled || showResult}
        className="inline-block mx-1 align-middle rounded-md border-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-colors text-center"
        style={{
          minWidth: `${gapMinWidth}px`,
          height: `${fontSize + 14}px`,
          padding: `0 8px`,
          backgroundColor: bg,
          borderColor,
          color,
          fontFamily,
          fontSize: `${fontSize}px`,
          fontWeight: 500,
        }}
      />
    );
  };

  const hideChrome = !isEditor;

  return (
    <div
      className={`w-full h-full rounded-xl overflow-hidden flex flex-col relative ${
        hideChrome ? '' : 'border-2 shadow-sm'
      }`}
      style={{
        backgroundColor: hideChrome ? 'transparent' : backgroundColor,
        borderColor: '#e5e7eb',
      }}
    >
      {/* Text area */}
      <div ref={scrollRef} className="flex-1 min-h-0 min-w-0 p-5 overflow-y-auto overflow-x-hidden">
        {segments.length === 0 ? (
          <p ref={contentRef} className="text-sm text-gray-400">
            {t('games.fillGaps.enterTextInSettings')}
          </p>
        ) : (
          <p
            ref={contentRef}
            style={{
              fontSize: `${fontSize}px`,
              fontFamily,
              lineHeight: 1.9,
              color: textColor,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'break-word',
            }}
          >
            {segments.map((seg, idx) => {
              if (seg.type === 'text') return <span key={`t_${idx}`}>{seg.value}</span>;
              if (seg.type === 'bold') return <strong key={`b_${idx}`}>{seg.value}</strong>;
              if (seg.type === 'italic') return <em key={`i_${idx}`}>{seg.value}</em>;
              if (seg.type === 'bolditalic') return <strong key={`bi_${idx}`}><em>{seg.value}</em></strong>;
              return <React.Fragment key={`g_${seg.key}_${idx}`}>{renderGap(seg.key)}</React.Fragment>;
            })}
          </p>
        )}
      </div>

      {/* Action bar */}
      {!isEditor && (
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-2">
          {showResult && allowRetry && !allCorrect && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg shadow-sm transition"
            >
              <RotateCcw size={14} />
              {t('games.common.tryAgain')}
            </button>
          )}
          {!showResult && !hideCheckButton && (
            <button
              onClick={handleCheck}
              disabled={!allFilled}
              className="px-4 py-1.5 text-sm font-medium rounded-lg shadow-sm transition text-white disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              style={{ backgroundColor: allFilled ? buttonColor : undefined }}
            >
              {t('games.common.check')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export { normalize as normalizeFillGapAnswer };
export default FillGapsElement;
